/**
 * The wire contract between the browser and the Pages Functions proxy.
 * Imported by both sides so there is exactly one definition.
 */

/** Google's price enum. PRICE_LEVEL_FREE exists in responses but is rejected in requests. */
export const PRICE_LEVELS = [
  'PRICE_LEVEL_INEXPENSIVE',
  'PRICE_LEVEL_MODERATE',
  'PRICE_LEVEL_EXPENSIVE',
  'PRICE_LEVEL_VERY_EXPENSIVE',
] as const;

export type PriceLevel = (typeof PRICE_LEVELS)[number];

/** Includes FREE, which only ever arrives in a response. */
export type ResponsePriceLevel = PriceLevel | 'PRICE_LEVEL_FREE';

export interface LatLng {
  lat: number;
  lng: number;
}

export interface SearchRequest {
  textQuery: string;
  center: LatLng;
  /** Metres. Feeds locationBias.circle; enforced properly by client-side haversine. */
  radiusM: number;
  /** A single Table A place type, e.g. "asian_restaurant". */
  includedType?: string;
  priceLevels?: PriceLevel[];
  openNow?: boolean;
  minRating?: number;
  rankPreference?: 'RELEVANCE' | 'DISTANCE';
}

export interface PlaceResult {
  id: string;
  name: string;
  address: string;
  location: LatLng;
  types: string[];
  primaryType?: string;
  googleMapsUri?: string;
  businessStatus?: string;
  priceLevel?: ResponsePriceLevel;
  rating?: number;
  userRatingCount?: number;
  openNow?: boolean;
  photoName?: string;
  /** Position in Google's own ordering, preserved for the relevance term in ranking. */
  rank: number;
}

export type BudgetLevel = 'ok' | 'warn' | 'critical' | 'exhausted';

/** Which of the three limits is currently closest to binding. */
export type BudgetScope = 'global' | 'user-daily' | 'user-monthly' | 'burst';

export interface BudgetStatus {
  used: number;
  limit: number;
  remaining: number;
  /** ISO date (UTC) when the binding limit resets. */
  resetsAt: string;
  level: BudgetLevel;
  scope: BudgetScope;
}

/** One of a person's own allowances, in the terms they'd describe it themselves. */
export interface UserAllowance {
  used: number;
  limit: number;
  remaining: number;
  /** ISO date (UTC) when this allowance refills. */
  resetsAt: string;
}

/**
 * What *this* account has spent and is allowed, as opposed to `BudgetStatus`, which
 * reports on whichever limit is closest to binding and is usually the shared pool.
 * The two answer different questions and both are worth showing.
 */
export interface UserUsage {
  daily: UserAllowance;
  monthly: UserAllowance;
}

export interface SearchResponse {
  places: PlaceResult[];
  budget: BudgetStatus;
  /** So the figure on screen updates without a second round trip after each search. */
  usage: UserUsage;
  cached: boolean;
}

export interface ApiError {
  error: string;
  /** Present on 429 so the UI can explain which limit was hit. */
  budget?: BudgetStatus;
  retryAfterSeconds?: number;
  /**
   * Present on the 403 every route returns while a password change is outstanding. The
   * status alone can't be told from an ordinary refusal, so this is what the client
   * keys off to show the change form.
   */
  passwordChangeRequired?: boolean;
}

export interface GeocodeResult {
  label: string;
  location: LatLng;
}

export interface SessionInfo {
  user: string;
  /** Whether to offer the user dashboard. The middleware, not this, is the gate. */
  admin: boolean;
  /** Signed in, but every route except the change form is refused until this clears. */
  mustChangePassword: boolean;
  /** The results ceiling in force for everyone - see `GlobalSettings`. */
  maxResults: number;
}

// ------------------------------------------------------- deployment settings

/**
 * How far "Show more" goes before the list stops growing.
 *
 * Fifteen is five pages of three, counting the first. Past that the list has stopped
 * being a shortlist and the honest move is to search again with a different answer,
 * which is why the button gives way to "Start over" rather than going on.
 */
export const DEFAULT_MAX_RESULTS = 15;
/** One page. Below this "Show more" could never appear at all. */
export const MIN_MAX_RESULTS = 3;
/**
 * One Google search returns at most 20 places (`MAX_RESULTS` in
 * `functions/lib/places.ts`), so a higher ceiling could never be reached.
 */
export const MAX_MAX_RESULTS = 20;

/**
 * Settings that apply to every account, editable from the dashboard rather than by
 * redeploying.
 *
 * `burst` is here and the other two rate limits are not, on purpose: daily and monthly
 * are the ones worth tuning per person, and each account can already override them.
 * Burst is an anti-runaway guard that should be the same number for everybody, and
 * `monthlyBudget` is the deployment's hard ceiling - the one figure that must not be
 * raisable from a browser session.
 */
export interface GlobalSettings {
  /** Most places the results list will ever show, however many matched. */
  maxResults: number;
  /** Searches per minute per account, unless that account overrides it. */
  burst: number;
}

/** The dashboard's view of the settings: what applies, what is stored, what it falls back to. */
export interface AdminSettingsView {
  /** What is in force right now - a stored value where there is one, else the default. */
  effective: GlobalSettings;
  /** Only what is actually stored, so a blank box can mean "inherit". */
  overrides: Partial<GlobalSettings>;
  /** Where an unset field falls back to: the built-in, or the var in wrangler.toml. */
  defaults: GlobalSettings;
}

// ---------------------------------------------------------------- admin

/**
 * Per-user rate-limit overrides. An absent field inherits the deployment default.
 *
 * Burst is not here: it is an anti-runaway guard, the same number for everyone, and
 * lives in `GlobalSettings`.
 */
export interface AdminUserLimits {
  daily?: number;
  monthly?: number;
}

export interface AdminUser {
  username: string;
  admin: boolean;
  /** The bootstrap admin. Cannot be demoted, disabled or deleted. */
  founder: boolean;
  disabled: boolean;
  /** Signed in but locked to the change form until they replace what they were given. */
  mustChangePassword: boolean;
  createdAt?: string;
  /** ISO 8601. Absent means no sign-in on record. */
  lastLoginAt?: string;
  /** Only what this account sets for itself, so the UI can show "inherited". */
  overrides: AdminUserLimits;
  /** What actually applies once inheritance is resolved. */
  effective: Required<AdminUserLimits>;
  /** Billed calls used in the current UTC day and month. */
  usage: { daily: number; monthly: number };
}

/**
 * Deployment-wide spend for the month, so an admin can see what the whole install is
 * costing without adding up the cards by hand.
 *
 * Only the month is here. It is the global counter the ceiling is actually enforced
 * against, so it stays right even after an account is deleted, and nothing on the
 * client could reconstruct it. Today's total is a plain sum of the accounts on screen,
 * so the view adds that up itself rather than holding a copy that goes stale the moment
 * someone is created or removed.
 */
export interface AdminTotals {
  month: number;
  /** The month's hard ceiling - `MONTHLY_BUDGET`. Not overridable per user. */
  monthlyBudget: number;
  /** `month` against `monthlyBudget`, classified server-side so the one set of
   *  thresholds in `functions/lib/budget.ts` stays the only set. */
  level: BudgetLevel;
}

export interface AdminUserList {
  users: AdminUser[];
  /** The deployment-wide limits, shown as the placeholder for an unset override. */
  defaults: Required<AdminUserLimits>;
  /** Who is asking, so the UI can grey out actions it knows will be refused. */
  self: string;
  totals: AdminTotals;
}

/** `password` is present only when the server generated one - it is never stored. */
export interface AdminUserSaved {
  user: AdminUser;
  password?: string;
}
